/**
 * Email verification.
 *
 * Shown when the anonymous allowance runs out, never before. Asking for an
 * address up front would cost more uploads than it gains: someone who has not
 * used the tool yet has no reason to hand one over.
 */

import { ensureTurnstile } from './turnstile-loader.js';

let dialog = null;

// Read the same meta tag app.js does, rather than take a dependency on it.
// Empty sitekey means the widget never renders and the token stays '', which
// the server (with an empty secret) accepts.
const TURNSTILE_SITEKEY =
  document.querySelector('meta[name="turnstile-sitekey"]')?.content?.trim() || '';

/**
 * @param {object} options
 * @param {number} options.limit how many the anonymous allowance was
 * @param {Function} options.send  (email) => Promise
 * @param {Function} options.confirm (email, code) => Promise
 * @returns {Promise<boolean>} true when verified
 */
export function requestVerification(options) {
  const { used, limit, verifiedLimit, send, confirm } = options;
  const extra = Math.max(0, verifiedLimit - used);

  if (!dialog) {
    dialog = document.createElement('dialog');
    dialog.className = 'confirm verify';
    document.body.appendChild(dialog);
  }

  dialog.innerHTML = `
    <div class="verify-body">
      <h2>Unlock ${extra} more today</h2>
      <p>
        You have used your ${limit} free log${limit === 1 ? '' : 's'}. Verify an
        email address for ${verifiedLimit} a day, ${extra} of them still
        available right now.
      </p>

      <label class="field">
        <span>Email address</span>
        <input type="email" id="verify-email" autocomplete="email"
               inputmode="email" placeholder="you@example.com" required>
      </label>

      <label class="field code" hidden>
        <span>Six-digit code</span>
        <input type="text" id="verify-code" inputmode="numeric" autocomplete="one-time-code"
               pattern="[0-9]{6}" maxlength="6" placeholder="000000">
      </label>

      <p class="verify-note" role="status" aria-live="polite"></p>

      <div class="turnstile-slot" id="verify-turnstile"></div>

      <label class="consent">
        <input type="checkbox" id="verify-marketing">
        <span>I'm a professional drone operator, please email me about how
          Dronedesk can help me save time and money. Monthly at most,
          unsubscribe any time.</span>
      </label>

      <p class="verify-privacy">
        We store your address so we can recognise you. Your flight logs stay in
        this browser either way. The code itself is not marketing, so you will
        get it whether or not you tick the box.
      </p>

      <div class="confirm-actions">
        <button type="button" data-cancel>Not now</button>
        <button type="button" class="primary" data-submit>Send me a code</button>
      </div>
    </div>`;

  const emailField = dialog.querySelector('#verify-email');
  const codeField = dialog.querySelector('#verify-code');
  const codeRow = dialog.querySelector('.field.code');
  const note = dialog.querySelector('.verify-note');
  const submit = dialog.querySelector('[data-submit]');
  const cancel = dialog.querySelector('[data-cancel]');

  // A fresh widget each time the dialog opens: innerHTML above replaced the
  // container, so any previous render is gone with it.
  let widgetId = null;

  const slot = dialog.querySelector('#verify-turnstile');

  // api.js is fetched on demand rather than on page load, so the widget lands a
  // moment after the dialog does. Awaiting it here would hold the modal shut
  // for a network round trip, so the render is left to catch up on its own and
  // act() waits on widgetReady before reading a token.
  //
  // The dialog element is reused between opens. If a later open has already
  // replaced the markup, this slot is detached and rendering into it would put
  // a widget nobody can see on a page nobody is looking at.
  const widgetReady = TURNSTILE_SITEKEY
    ? ensureTurnstile().then((ready) => {
        if (!ready || !slot || !slot.isConnected) return;

        widgetId = turnstile.render(slot, {
          sitekey: TURNSTILE_SITEKEY,
          appearance: document.querySelector('meta[name="turnstile-appearance"]')?.content?.trim() || 'interaction-only',
        });
      })
    : Promise.resolve();

  let stage = 'email';

  const say = (message, tone = '') => {
    note.textContent = message;
    note.className = `verify-note ${tone}`;
  };

  const busy = (on, label) => {
    submit.disabled = on;
    submit.innerHTML = on
      ? `<span class="spinner"></span>${label}`
      : label;
  };

  return new Promise((resolve) => {
    let settled = false;

    const finish = (value) => {
      if (settled) return;
      settled = true;
      dialog.close();
      resolve(value);
    };

    const act = async () => {
      if (stage === 'email') {
        const email = emailField.value.trim();

        // The browser's own check first: no point spending a request on
        // something that is not an address at all.
        if (!emailField.checkValidity() || email === '') {
          say('Enter an email address you can receive mail at.', 'bad');
          emailField.focus();
          return;
        }

        // The human check. The widget is fetched on demand, so wait for it to
        // exist before reading it, then refuse to spend a request on a send the
        // server would only reject.
        await widgetReady;

        const token = widgetId !== null ? turnstile.getResponse(widgetId) : '';

        if (TURNSTILE_SITEKEY && !token) {
          say('Just a moment while we confirm you are human, then try again.', 'bad');
          return;
        }

        busy(true, 'Sending…');

        try {
          await send(email, token);

          stage = 'code';
          codeRow.hidden = false;
          submit.textContent = 'Verify';
          say('Code sent. It expires in ten minutes.', 'good');
          codeField.focus();
        } catch (error) {
          say(error.message || 'We could not send that code.', 'bad');
        } finally {
          // A token is single use. Reset so a resend, or a retry after an
          // error, has a fresh one waiting.
          if (widgetId !== null) turnstile.reset(widgetId);
          busy(false, stage === 'code' ? 'Verify' : 'Send me a code');
        }

        return;
      }

      const code = codeField.value.trim();

      if (!/^[0-9]{6}$/.test(code)) {
        say('Enter the six digits from the email.', 'bad');
        codeField.focus();
        return;
      }

      busy(true, 'Checking…');

      try {
        await confirm(
          emailField.value.trim(),
          code,
          dialog.querySelector('#verify-marketing').checked
        );
        finish(true);
      } catch (error) {
        say(error.message || 'That code was not accepted.', 'bad');
        codeField.select();
      } finally {
        busy(false, 'Verify');
      }
    };

    submit.addEventListener('click', act);
    cancel.addEventListener('click', () => finish(false));

    // Enter should submit whichever field is in play, which is what anyone
    // typing a code into a box expects.
    for (const field of [emailField, codeField]) {
      field.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          act();
        }
      });
    }

    // Pasting a code from a mail client often brings whitespace with it.
    codeField.addEventListener('input', () => {
      codeField.value = codeField.value.replace(/\D/g, '').slice(0, 6);

      if (codeField.value.length === 6) act();
    });

    dialog.addEventListener('close', () => finish(false), { once: true });
    dialog.showModal();
    emailField.focus();
  });
}
